import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ServiceTypeEnum,
  adminPostLocationReqDtoSchema,
  useAdminCustomerControllerGetOne,
  useAdminLocationControllerCreate,
} from "service";
import { useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { generateRoute, makeSxStyles } from "ui";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { toast } from "react-toastify";
import { AdminPostLocationReqDto } from "service/api/estabrookSchemas";
import { AdRoutes } from "../../../constants/ad-routes";
import { fullName } from "../../../utils/string-helpers";
import { toFormikValidationSchema } from "zod-formik-adapter";
import useAddressValidation from "../../../hooks/use-address-validation";
import CrmDetailsFields, {
  CrmFieldValues,
} from "@/shared/components/crm/CrmDetailsFields";
import CustomerFinancesSection from "@/shared/components/crm/CustomerFinancesSection";
import { fieldLabelSx } from "@/shared/components/crm/form-typography";
import { useNumericFields } from "@/shared/hooks/useNumericFields";
import { numericFieldSx } from "@/shared/utils/numeric-input";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import Navbar from "../../../components/navbar/navbar";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { queryClient } from "../../../query-client";
import { createCustomerLocationsQueryKey } from "service/api/estabrookQueryKeys";

type AddressFormValues = AdminPostLocationReqDto & {
  reportsEnabled: boolean;
} & CrmFieldValues;

const AddAddressPage: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  // CRM sections (Onsite Contact + Driveway Info) collapse behind Show More.
  const [showMore, setShowMore] = useState(false);
  const { data: dataCustomer } = useAdminCustomerControllerGetOne({
    pathParams: { customerId: customerId || "" },
  });
  const navigate = useNavigate();
  const {
    getPlacePredictions,
    placePredictions,
    createAddressValidationSchema,
  } = useAddressValidation();
  const { isLoading, mutate } = useAdminLocationControllerCreate({
    onSuccess: (data) => {
      toast.success("Address Created Successfully");
      resetForm();
      // Invalidate the customer locations query so the new address appears in tabs
      if (data?.data?.customerId) {
        queryClient.invalidateQueries(
          createCustomerLocationsQueryKey(data.data.customerId),
        );
      }
      setTimeout(() => {
        navigate(
          generateRoute(AdRoutes.customerDetailAddress, {
            customerId: customerId!,
            addressId: data?.data?.id || "",
          }),
          { replace: true },
        );
      }, 10);
    },
  });
  const styles = useStyles();

  const {
    values,
    dirty,
    isValid,
    setFieldValue,
    setFieldTouched,
    handleChange,
    submitForm,
    handleBlur,
    touched,
    errors,
    resetForm,
    validateField,
  } = useFormik<AddressFormValues>({
    initialValues: {
      address: "",
      billingInformation: "",
      notes: "",
      serviceTime: 0,
      customerId: customerId || "",
      type: ServiceTypeEnum.annual,
      reportsEnabled: false,
      regionId: "",
      // CRM fields — persisted atomically via locationDetails on the create call.
      onsiteContactName: "",
      onsiteContactEmail: "",
      contractPrice: undefined,
      billingAddress: "",
      financialNotes: "",
      squareFootage: undefined,
      slope: undefined,
      condition: undefined,
      difficulty: undefined,
    },
    validationSchema: toFormikValidationSchema(
      createAddressValidationSchema(adminPostLocationReqDtoSchema),
    ),
    onSubmit: (values) =>
      mutate({
        body: {
          address: values.address,
          notes: values.notes,
          serviceTime: values.serviceTime,
          customerId: values.customerId,
          type: values.type,
          reportsEnabled: values.reportsEnabled,
          regionId: values.regionId,
          locationDetails: {
            onsiteContactName: values.onsiteContactName || undefined,
            onsiteContactEmail: values.onsiteContactEmail || undefined,
            contractPrice: values.contractPrice,
            billingAddress: values.billingAddress || undefined,
            financialNotes: values.financialNotes || undefined,
            squareFootage: values.squareFootage,
            slope: values.slope,
            condition: values.condition,
            difficulty: values.difficulty,
          },
        },
      }),
  });

  useUnsavedChangesBlocker(dirty);

  const numeric = useNumericFields({
    setFieldValue,
    setFieldTouched,
    handleBlur,
  });

  // A positive magnitude in minutes: no typed "-"/"e"/letters and no stepping,
  // with the schema's ">= 1" rule enforcing the sign. reportUntouched is off
  // because the form starts at 0, which fails that rule — the message waits for
  // the field to be used rather than greeting an untouched form.
  const serviceTime = numeric.field({
    name: "serviceTime",
    value: values.serviceTime,
    error: errors.serviceTime,
    touched: touched.serviceTime,
    allowDecimal: true,
    reportUntouched: false,
  });

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Customer Management",
                url: AdRoutes.customer,
              },
              {
                label: fullName(dataCustomer?.data),
                url: generateRoute(AdRoutes.customerDetailAddress, {
                  customerId: customerId || "",
                  addressId: "",
                }),
              },
              {
                label: "Add New Address",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid="new-customer-combo"
            >
              {"Create Address"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.customerDetailContainer}>
        <Typography sx={styles.sectionTitle}>Address Info</Typography>
        <Typography sx={fieldLabelSx}>Address *</Typography>
        <Autocomplete
          freeSolo
          filterOptions={(x) => x}
          options={placePredictions.map((prediction) => {
            return { label: prediction.description, value: prediction };
          })}
          value={values.address}
          onChange={(_evt, value) => {
            if (value && typeof value === "object") {
              setFieldValue("address", String(value.label));
              setFieldTouched("address", true);
              // Immediately validate to clear any existing errors
              setTimeout(() => {
                validateField("address");
              }, 0);
            } else if (typeof value === "string") {
              setFieldValue("address", value);
              setFieldTouched("address", true);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="address"
              name="address"
              error={touched.address && !!errors.address}
              helperText={touched.address && errors.address}
              onChange={(evt) => {
                getPlacePredictions({ input: evt.target.value?.trim() });
                setFieldValue("address", evt.target.value);
                setFieldTouched("address", true);
              }}
              onBlur={() => {
                setFieldTouched("address", true);
                // Note: Formik will automatically validate on blur due to the validation schema
              }}
            />
          )}
        />
        <Typography sx={fieldLabelSx}>Region</Typography>
        <TextField
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          sx={styles.regionTextField}
          value={"Unassigned"}
          disabled
        />
        <Typography sx={fieldLabelSx}>Service Duration *</Typography>
        <TextField
          id="serviceTime"
          type="number"
          margin="dense"
          variant="outlined"
          value={serviceTime.value}
          onChange={serviceTime.onChange}
          onFocus={serviceTime.onFocus}
          onBlur={serviceTime.onBlur}
          error={serviceTime.showError}
          helperText={serviceTime.helperText}
          inputProps={serviceTime.inputProps}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">minutes</InputAdornment>
            ),
          }}
          sx={numericFieldSx}
        />
        <Typography sx={fieldLabelSx}>Snow Reporter</Typography>
        <Checkbox
          id="reportsEnabled"
          checked={values.reportsEnabled}
          sx={{ justifySelf: "flex-start" }}
          onChange={handleChange}
        />
        <Box sx={{ gridColumn: "1 / -1" }}>
          <CustomerFinancesSection
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
          {showMore && (
            <CrmDetailsFields
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              sectionOrder={["onsiteContact", "drivewayInfo"]}
            />
          )}
          <Button
            variant="text"
            onClick={() => setShowMore(!showMore)}
            endIcon={showMore ? <ArrowDropUp /> : <ArrowDropDown />}
            sx={{ mt: "8px" }}
            data-testid={showMore ? "show-less-button" : "show-more-button"}
          >
            {showMore ? "Show Less" : "Show More"}
          </Button>
        </Box>
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  sectionTitle: {
    gridColumn: "1 / -1",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
  },
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    // Match FieldRow (label 260 / gap 24 / input 360) so Address Info aligns
    // with the Finances / Onsite / Driveway sections below.
    gridTemplateColumns: "260px 360px",
    columnGap: "24px",
    rowGap: "16px",
    alignItems: "center",
  },
  regionTextField: {
    minHeight: "53px",
    justifyContent: "center",
    margin: "0",
  },
}));

export default AddAddressPage;
